import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import { Layout } from "@/components/Layout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PageTransition } from "@/components/PageTransition";
import Home from "@/pages/Home";
import ContactPage from "@/pages/ContactPage";
import ScaffoldedPage from "@/pages/ScaffoldedPage";
import NotFound from "@/pages/NotFound";
import ToolsIndex from "@/pages/tools/ToolsIndex";
import PDPLReadiness from "@/pages/tools/PDPLReadiness";
import ISO27001 from "@/pages/tools/ISO27001";
import SoftwareCost from "@/pages/tools/SoftwareCost";
import AIOpportunity from "@/pages/tools/AIOpportunity";
import AdminLeads from "@/pages/AdminLeads";
import AdminCareers from "@/pages/AdminCareers";
import AboutPage from "@/pages/company/AboutPage";
import HowWeWorkPage from "@/pages/company/HowWeWorkPage";
import PDPLPage from "@/pages/company/PDPLPage";
import CareersPage from "@/pages/company/CareersPage";
import JobDetail from "@/pages/company/JobDetail";
import CompanyContactPage from "@/pages/company/CompanyContactPage";
import CompanyIndex from "@/pages/company/CompanyIndex";
import { ServicePage, IndustryPage, SolutionPage, CaseStudyPage, AiLabPage } from "@/pages/inner/DynamicInnerPage";
import { ServicesIndex, IndustriesIndex, SolutionsIndex, WorkIndex, ProductsIndex } from "@/pages/inner/Indexes";
import { MeyarOverview, MeyarEnterpriseGRC, MeyarCyberSecurityGRC } from "@/pages/inner/MeyarPages";
import ResourcesIndex from "@/pages/resources/ResourcesIndex";
import BlogIndex from "@/pages/resources/BlogIndex";
import BlogPost from "@/pages/resources/BlogPost";
import Podcast from "@/pages/resources/Podcast";
import Downloads from "@/pages/resources/Downloads";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import TermsOfService from "@/pages/legal/TermsOfService";

// Arabic pages — Batch 1
import HomeAr from "@/pages/ar/HomeAr";
import AboutUsAr from "@/pages/ar/AboutUsAr";
import HowWeWorkAr from "@/pages/ar/HowWeWorkAr";
import ContactAr from "@/pages/ar/ContactAr";
import PdplAr from "@/pages/ar/PdplAr";
import MeyarOverviewAr from "@/pages/ar/MeyarOverviewAr";
// Arabic pages — Batch 2 (Services & Industries)
import ServicesIndexAr from "@/pages/ar/ServicesIndexAr";
import IndustriesIndexAr from "@/pages/ar/IndustriesIndexAr";
import { ServicePageAr, IndustryPageAr, AiLabPageAr, SolutionPageAr, CaseStudyPageAr } from "@/pages/ar/DynamicArPages";
// Arabic pages — Batch 3 (Solutions, Products, Work, Meyar products)
import SolutionsIndexAr from "@/pages/ar/SolutionsIndexAr";
import ProductsIndexAr from "@/pages/ar/ProductsIndexAr";
import WorkIndexAr from "@/pages/ar/WorkIndexAr";
import { MeyarEnterpriseGrcAr, MeyarCyberSecurityGrcAr } from "@/pages/ar/MeyarProductsAr";
// Arabic pages — Batch 4 (Resources, Blog, Podcast, Careers, Legal)
import { ResourcesIndexAr, BlogIndexAr, BlogPostAr, PodcastAr, CareersAr } from "@/pages/ar/ResourcesCareersAr";
import { PrivacyPolicyAr, TermsOfServiceAr, CookiePolicyAr } from "@/pages/ar/LegalAr";
// Arabic pages — Batch 5 (Tools, Downloads, Job detail)
import { ToolsIndexAr, PdplReadinessAr, IsoReadinessAr, SoftwareCostAr, AiOpportunityAr } from "@/pages/ar/ToolsAr";
import { DownloadsAr, JobDetailAr } from "@/pages/ar/DownloadsJobAr";

const SCAFFOLDS = {
  "/who-we-serve": { category: "Who we serve", title: "Built for businesses that have outgrown their systems.", intro: "Owners, operators and technology leaders come to us when the software that got them here won't take them any further." },
  "/sitemap": { category: "Sitemap", title: "Sitemap", intro: "An index of every page on the site." },
};

// English routes shared between /* and (fallback for) /ar/*. Only the six
// pages listed in the AR Batch 1 have a real Arabic override at the moment;
// every other /ar/* route falls through to its English component with the
// language toggle flipping copy in place as more bundles land.
function PublicRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/tools" element={<ToolsIndex />} />
      <Route path="/tools/pdpl-readiness-check" element={<PDPLReadiness />} />
      <Route path="/tools/iso-27001-readiness" element={<ISO27001 />} />
      <Route path="/tools/software-cost-estimator" element={<SoftwareCost />} />
      <Route path="/tools/ai-opportunity-finder" element={<AIOpportunity />} />
      <Route path="/services" element={<ServicesIndex />} />
      <Route path="/services/:slug" element={<ServicePage />} />
      <Route path="/ai-lab" element={<AiLabPage />} />
      <Route path="/industries" element={<IndustriesIndex />} />
      <Route path="/industries/:slug" element={<IndustryPage />} />
      <Route path="/solutions" element={<SolutionsIndex />} />
      <Route path="/solutions/grocery-delivery-app" element={<Navigate to="/solutions" replace />} />
      <Route path="/solutions/shopify-headless-commerce" element={<Navigate to="/solutions" replace />} />
      <Route path="/solutions/:slug" element={<SolutionPage />} />
      <Route path="/products" element={<ProductsIndex />} />
      <Route path="/meyar" element={<MeyarOverview />} />
      <Route path="/meyar/enterprise-grc" element={<MeyarEnterpriseGRC />} />
      <Route path="/meyar/cyber-security-grc" element={<MeyarCyberSecurityGRC />} />
      <Route path="/work" element={<WorkIndex />} />
      <Route path="/work/:slug" element={<CaseStudyPage />} />
      <Route path="/resources" element={<ResourcesIndex />} />
      <Route path="/resources/blog" element={<BlogIndex />} />
      <Route path="/resources/blog/:slug" element={<BlogPost />} />
      <Route path="/resources/podcast" element={<Podcast />} />
      <Route path="/resources/downloads" element={<Downloads />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/podcast" element={<Podcast />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/company" element={<CompanyIndex />} />
      <Route path="/company/about-us" element={<AboutPage />} />
      <Route path="/company/how-we-work" element={<HowWeWorkPage />} />
      <Route path="/company/pdpl-compliance-oman" element={<PDPLPage />} />
      <Route path="/company/careers" element={<CareersPage />} />
      <Route path="/company/careers/:slug" element={<JobDetail />} />
      <Route path="/company/contact" element={<CompanyContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-we-work" element={<HowWeWorkPage />} />
      <Route path="/pdpl-compliance" element={<PDPLPage />} />
      <Route path="/careers" element={<CareersPage />} />
      {Object.entries(SCAFFOLDS).map(([path, props]) => (
        <Route key={path} path={path} element={<ScaffoldedPage {...props} />} />
      ))}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function ArabicRoutes() {
  return (
    <Routes>
      {/* Batch 1 — real Arabic components */}
      <Route path="/" element={<HomeAr />} />
      <Route path="/company/about-us" element={<AboutUsAr />} />
      <Route path="/company/how-we-work" element={<HowWeWorkAr />} />
      <Route path="/company/contact" element={<ContactAr />} />
      <Route path="/company/pdpl-compliance-oman" element={<PdplAr />} />
      <Route path="/meyar" element={<MeyarOverviewAr />} />

      {/* Batch 2 — Services & Industries */}
      <Route path="/services" element={<ServicesIndexAr />} />
      <Route path="/services/:slug" element={<ServicePageAr />} />
      <Route path="/ai-lab" element={<AiLabPageAr />} />
      <Route path="/industries" element={<IndustriesIndexAr />} />
      <Route path="/industries/:slug" element={<IndustryPageAr />} />

      {/* Batch 3 — Solutions, Products, Work, Meyar products */}
      <Route path="/solutions" element={<SolutionsIndexAr />} />
      <Route path="/solutions/:slug" element={<SolutionPageAr />} />
      <Route path="/products" element={<ProductsIndexAr />} />
      <Route path="/work" element={<WorkIndexAr />} />
      <Route path="/work/:slug" element={<CaseStudyPageAr />} />
      <Route path="/meyar/enterprise-grc" element={<MeyarEnterpriseGrcAr />} />
      <Route path="/meyar/cyber-security-grc" element={<MeyarCyberSecurityGrcAr />} />

      {/* Batch 4 — Resources, Blog, Podcast, Careers, Legal */}
      <Route path="/resources" element={<ResourcesIndexAr />} />
      <Route path="/resources/blog" element={<BlogIndexAr />} />
      <Route path="/resources/blog/:slug" element={<BlogPostAr />} />
      <Route path="/resources/podcast" element={<PodcastAr />} />
      <Route path="/blog" element={<BlogIndexAr />} />
      <Route path="/podcast" element={<PodcastAr />} />
      <Route path="/company/careers" element={<CareersAr />} />
      <Route path="/careers" element={<CareersAr />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyAr />} />
      <Route path="/terms-of-service" element={<TermsOfServiceAr />} />
      <Route path="/cookie-policy" element={<CookiePolicyAr />} />

      {/* Batch 5 — Tools, Downloads, Job detail */}
      <Route path="/tools" element={<ToolsIndexAr />} />
      <Route path="/tools/pdpl-readiness-check" element={<PdplReadinessAr />} />
      <Route path="/tools/iso-27001-readiness" element={<IsoReadinessAr />} />
      <Route path="/tools/software-cost-estimator" element={<SoftwareCostAr />} />
      <Route path="/tools/ai-opportunity-finder" element={<AiOpportunityAr />} />
      <Route path="/resources/downloads" element={<DownloadsAr />} />
      <Route path="/company/careers/:slug" element={<JobDetailAr />} />
      <Route path="/careers/:slug" element={<JobDetailAr />} />

      {/* Everything else falls through to the English component. The i18n
          provider still reports lang="ar" + dir="rtl" for these pages so the
          layout mirrors correctly; content will be swapped as later batches
          land. */}
      <Route path="*" element={<PublicRoutes />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <ScrollToTop />
            <Routes>
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/careers" element={<AdminCareers />} />
              <Route
                path="/ar/*"
                element={
                  <Layout>
                    <PageTransition>
                      <ArabicRoutes />
                    </PageTransition>
                  </Layout>
                }
              />
              <Route
                path="/*"
                element={
                  <Layout>
                    <PageTransition>
                      <PublicRoutes />
                    </PageTransition>
                  </Layout>
                }
              />
            </Routes>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
